import { Fragment, useRef, useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { useBusy } from '../../hooks/useBusy';
import api from '../../api/client';
import { formatWarning, type ApiWarning } from '../../hooks/useApiCall';
import { useI18n, formatDay } from '../../i18n';
import { useLabels, countLabel } from '../../i18n/labels';
import ModalBackdrop from '../shared/ModalBackdrop';
import ModalHeader from '../shared/ModalHeader';
import ModalBody from '../shared/ModalBody';
import ModalFooter from '../shared/ModalFooter';
import Field from '../shared/Field';
import ConfirmModal from '../shared/ConfirmModal';
import GenderBadge from '../shared/GenderBadge';
import BoutLevelPill from '../bout/BoutLevelPill';
import { ClipboardIcon, LockIcon, PencilIcon, WarningIcon } from '../shared/Icons';
import { personName } from '../../utils/personName';
import { MAX_SIDE_JUDGES, MIN_SIDE_JUDGES, deriveAgeGroup, deriveWeightClass } from '../../utils/tournament';
import type { BoutLevel, Ring, TournamentReferee } from '../../types';

/** A person of a plan row: the name parts, never a joined name (#479). */
interface PersonRef {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
}
/** One official of a suggested panel. `neutral` is unknown (undefined) for
 *  a moved official until the check of the swap answers. */
interface OfficialRef extends PersonRef {
  neutral?: boolean;
  edited?: boolean;
}
interface Corner {
  first_name: string | null;
  last_name: string | null;
  team_name: string | null;
  gender: string | null;
  age_group: string | null;
  weight_class: string | null;
}
interface BoutAssignment {
  bout_id: string;
  bout_number: number;
  bout_date: string | null;
  /** The place of the bout in the order of its ring on its day (#589). */
  ring_day_order: number;
  bout_age_group: string | null;
  bout_weight_class: string | null;
  /** #623 — the level of the bout (null with an empty corner). */
  level: BoutLevel | null;
  red: Corner | null;
  blue: Corner | null;
  jury: PersonRef | null;
  /** The stored officials of the bout. */
  now: { ring_referee: PersonRef | null; side_judges: PersonRef[] };
  /** #589: the bout keeps its stored side judges, and its side cells are locked. */
  kept_side_judges: boolean;
  warnings: ApiWarning[];
  ring_referee: OfficialRef | null;
  side_judges: (OfficialRef | null)[];
}
interface PlanOfficialsSummary {
  fully_neutral: number;
  has_conflicts: boolean;
  total_bouts: number;
}
interface PlanOfficialsResult {
  assignments: BoutAssignment[];
  summary: PlanOfficialsSummary;
}
/** The answer of `check-officials`: the warnings and the flags of each panel. */
interface CheckAnswer {
  assignments: Array<{
    bout_id: string;
    warnings: ApiWarning[];
    ring_referee: { id: string; neutral: boolean } | null;
    side_judges: Array<{ id: string; neutral: boolean }>;
  }>;
  summary: PlanOfficialsSummary;
}

interface Props {
  rings: Ring[];
  referees: TournamentReferee[];
  tournamentId: string;
  onClose: () => void;
  /** The write committed (#664): the parent closes the modal and reads the
   *  tournament again. The read rejects when it fails. */
  onApplied: () => Promise<unknown>;
}

/** A cell of the plan: the ring seat, or a side seat by its index. */
type Slot = 'ring' | number;
interface Cell { row: number; slot: Slot }

/** The side seats that the planner fills (`PLAN_SIDE_SEATS` of the server). */
const PLAN_SIDE_SEATS = MIN_SIDE_JUDGES;

// 12 distinct colours, a scanning aid of the manager. #589 — the index of the
// person in the rotation gives the colour, so the first 12 people get 12
// different colours (a hash gave two people one colour). Not stored.
const REF_PALETTE = [
  { chip: 'bg-red-100', cell: 'bg-red-100 text-red-800' },
  { chip: 'bg-orange-100', cell: 'bg-orange-100 text-orange-800' },
  { chip: 'bg-amber-100', cell: 'bg-amber-100 text-amber-800' },
  { chip: 'bg-lime-100', cell: 'bg-lime-100 text-lime-800' },
  { chip: 'bg-green-100', cell: 'bg-green-100 text-green-800' },
  { chip: 'bg-teal-100', cell: 'bg-teal-100 text-teal-800' },
  { chip: 'bg-cyan-100', cell: 'bg-cyan-100 text-cyan-800' },
  { chip: 'bg-blue-100', cell: 'bg-blue-100 text-blue-800' },
  { chip: 'bg-indigo-100', cell: 'bg-indigo-100 text-indigo-800' },
  { chip: 'bg-violet-100', cell: 'bg-violet-100 text-violet-800' },
  { chip: 'bg-fuchsia-100', cell: 'bg-fuchsia-100 text-fuchsia-800' },
  { chip: 'bg-pink-100', cell: 'bg-pink-100 text-pink-800' },
];
const NO_COLOUR = { chip: 'bg-gray-100', cell: 'bg-gray-100 text-gray-700' };
const colourAt = (index: number) => (index < 0 ? NO_COLOUR : REF_PALETTE[index % REF_PALETTE.length]);

function getSlot(a: BoutAssignment, slot: Slot): OfficialRef | null {
  return slot === 'ring' ? a.ring_referee : a.side_judges[slot] ?? null;
}
function setSlot(a: BoutAssignment, slot: Slot, val: OfficialRef | null): void {
  if (slot === 'ring') a.ring_referee = val;
  else a.side_judges[slot] = val;
}
/** The people that a bout ends with at Apply All: the ring referee (the
 *  planned one, else the stored one, H4) and the side judges. */
function seatedIds(a: BoutAssignment): string[] {
  const ring = a.ring_referee?.id ?? a.now.ring_referee?.id;
  return [...(ring ? [ring] : []), ...a.side_judges.filter((s): s is OfficialRef => !!s).map(s => s.id)];
}
/** The body of check-officials and apply-officials: the ids only. A drop on an
 *  empty cell leaves null in a side seat (#588): send only the filled seats.
 *  #627 — a bout with a kept side panel says so: when a judge leaves the bout
 *  after the plan, Apply All keeps the stored side judges and does not seat
 *  that judge again. */
function panelsOf(plan: PlanOfficialsResult) {
  return plan.assignments.map(a => ({
    bout_id: a.bout_id,
    ring_referee: a.ring_referee ? { id: a.ring_referee.id } : null,
    side_judges: a.side_judges.filter((s): s is OfficialRef => !!s).map(s => ({ id: s.id })),
    ...(a.kept_side_judges ? { kept_side_judges: true } : {}),
  }));
}
/** True when Apply All changes the officials of the bout. */
function changes(a: BoutAssignment): boolean {
  const ring = !!a.ring_referee && a.ring_referee.id !== a.now.ring_referee?.id;
  if (a.kept_side_judges) return ring;
  const planned = a.side_judges.filter((s): s is OfficialRef => !!s).map(s => s.id).sort().join();
  return ring || planned !== a.now.side_judges.map(s => s.id).sort().join();
}
/** The flags and the reasons of a check, on the latest state of the plan. */
function mergeCheck(plan: PlanOfficialsResult, answer: CheckAnswer): PlanOfficialsResult {
  const rows = new Map(answer.assignments.map(r => [r.bout_id, r]));
  return {
    summary: answer.summary,
    assignments: plan.assignments.map(a => {
      const r = rows.get(a.bout_id);
      if (!r) return a;
      return {
        ...a,
        warnings: r.warnings,
        ring_referee: a.ring_referee && {
          ...a.ring_referee, neutral: r.ring_referee?.id === a.ring_referee.id ? r.ring_referee.neutral : undefined,
        },
        side_judges: a.side_judges.map(s => s && { ...s, neutral: r.side_judges.find(x => x.id === s.id)?.neutral }),
      };
    }),
  };
}

export default function PlanOfficialsModal({ rings, referees, tournamentId, onClose, onApplied }: Props) {
  const { showError, showToast } = useToast();
  const afterWrite = useAfterWrite();
  const { t, lang } = useI18n();
  const { boutCount } = useLabels();
  const [ring, setRing] = useState('');
  const [refs, setRefs] = useState<Set<string>>(new Set());
  const [ringRefs, setRingRefs] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<PlanOfficialsResult | null>(null);
  /** The rotation of the shown plan, in the order of the pool: it gives the colours. */
  const [rotation, setRotation] = useState<string[]>([]);
  /** The ring of the shown plan. The select can change while Generate runs,
   *  so a check sends this ring, not the value of the select. */
  const [planRing, setPlanRing] = useState('');
  const suggest = useBusy();
  const applyBusy = useBusy();
  // #588 — which apply runs: only its button shows the busy label.
  const [applying, setApplying] = useState<'all' | 'ring' | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [drag, setDrag] = useState<Cell | null>(null);
  const [dragOver, setDragOver] = useState<Cell | null>(null);
  /** #589 — the latest check of a swap. A late answer of an older check never
   *  overwrites a newer state (logic-reviewer §15). */
  const checkSeq = useRef(0);

  // The rotation that the boxes of step 1 make: its order gives the colours.
  const picked = referees.filter(r => refs.has(r.id)).map(r => r.id);

  /** The people of the legend: the rotation, then any other person in a
   *  cell (a kept side judge, #589). Each with the seats of the current plan. */
  const legend = (() => {
    if (!result) return [];
    const people = new Map<string, PersonRef & { ring: number; side: number }>();
    for (const id of rotation) {
      const r = referees.find(x => x.id === id);
      people.set(id, { id, first_name: r?.first_name, last_name: r?.last_name, ring: 0, side: 0 });
    }
    const count = (o: OfficialRef | null, seat: 'ring' | 'side') => {
      if (!o) return;
      const p = people.get(o.id) ?? { id: o.id, first_name: o.first_name, last_name: o.last_name, ring: 0, side: 0 };
      p[seat] += 1;
      people.set(o.id, p);
    };
    for (const a of result.assignments) {
      count(a.ring_referee, 'ring');
      a.side_judges.forEach(s => count(s, 'side'));
    }
    return [...people.values()];
  })();
  const colourIndex = new Map(legend.map((p, i) => [p.id, i]));
  /** #589 — the novices of the pool: the field comes to a manager of the tournament. */
  const novices = new Set(referees.filter(r => r.is_novice).map(r => r.id));
  const colourOf = (id: string) => colourAt(colourIndex.get(id) ?? -1);

  /** Ask the server for the warnings and the flags of the current panels. */
  async function check(plan: PlanOfficialsResult) {
    const seq = ++checkSeq.current;
    try {
      const res = await api.post<CheckAnswer>(`/api/tournaments/${tournamentId}/check-officials`, {
        ring_id: planRing, assignments: panelsOf(plan),
      });
      if (seq !== checkSeq.current) return; // a newer swap sent a newer check
      setResult(prev => prev && mergeCheck(prev, res.data));
    } catch (err) {
      if (seq === checkSeq.current) showError(err);
    }
  }

  function swapSlots(src: Cell, dst: Cell) {
    if (!result || (src.row === dst.row && src.slot === dst.slot)) return;
    const next: PlanOfficialsResult = {
      ...result, assignments: result.assignments.map(a => ({ ...a, side_judges: [...a.side_judges] })),
    };
    const from = getSlot(next.assignments[src.row], src.slot);
    const to = getSlot(next.assignments[dst.row], dst.slot);
    // The flags of a moved official belong to its old bout: unknown until the check answers.
    setSlot(next.assignments[src.row], src.slot, to ? { ...to, edited: true, neutral: undefined } : null);
    setSlot(next.assignments[dst.row], dst.slot, from ? { ...from, edited: true, neutral: undefined } : null);
    // H1 and H4 on the rows of the swap, before the call: Apply All refuses them.
    // In one row, the two swapped seats change places and are no duplicate.
    for (const row of new Set([src.row, dst.row])) {
      const a = next.assignments[row];
      if (a.ring_referee && a.jury && a.ring_referee.id === a.jury.id) {
        showToast(t.errors.JURY_CANNOT_BE_RING_REF, 'error');
        return;
      }
      const ids = seatedIds(a);
      if (new Set(ids).size !== ids.length) {
        showToast(t.tournament.cannotPlaceReferee, 'error');
        return;
      }
    }
    setResult(next);
    void check(next);
  }

  function generate() {
    return suggest.run(async () => {
      const ringId = ring;
      try {
        const res = await api.post<PlanOfficialsResult>(`/api/tournaments/${tournamentId}/suggest-officials`, {
          ring_id: ringId,
          referee_ids: picked,
          ring_ref_candidate_ids: [...ringRefs],
        });
        checkSeq.current++; // a late check of an older plan does not reach this one
        setRotation(picked);
        setPlanRing(ringId);
        setResult(res.data);
      } catch (err) { showError(err); }
    });
  }

  /** #588 — `ringOnly` writes the ring referees and keeps the stored side
   *  judges. At a live event the side judges join at the live bout. */
  function apply(ringOnly: boolean) {
    return applyBusy.run(async () => {
      if (!result) return;
      setApplying(ringOnly ? 'ring' : 'all');
      try {
        const assignments = panelsOf(result);
        const res = await api.post<{ applied: number; warnings?: ApiWarning[] }>(
          `/api/tournaments/${tournamentId}/apply-officials`,
          ringOnly ? { assignments, ring_referees_only: true } : { assignments });
        // #440: a bout that went live after the plan is skipped, with a warning.
        // #589: a bout with more than 3 side judges keeps them, with a warning.
        // The advice replaces the success text: no success with a warning.
        const warnings = res.data.warnings ?? [];
        const success = ringOnly
          ? countLabel(lang, res.data.applied, { one: t.planOfficials.ringRefsAssignedOne, few: t.planOfficials.ringRefsAssignedFew, other: t.planOfficials.ringRefsAssignedOther })
          : countLabel(lang, res.data.applied, { one: t.tournament.officialsAssignedOne, few: t.tournament.officialsAssignedFew, other: t.tournament.officialsAssignedOther });
        await afterWrite({
          source: 'PlanOfficialsModal:apply',
          success: warnings.length ? undefined : success,
          warnings,
          refresh: onApplied,
        });
      } catch (err) { showError(err); }
      finally {
        setApplying(null);
        setConfirmAll(false);
      }
    });
  }

  const back = () => { checkSeq.current++; setResult(null); };

  // #589 — a bout with a kept side panel shows its 4 or 5 kept side judges in more columns.
  const judgeCols = result
    ? Math.min(MAX_SIDE_JUDGES, Math.max(PLAN_SIDE_SEATS, ...result.assignments.map(a => a.side_judges.length)))
    : PLAN_SIDE_SEATS;
  const changeCount = result ? result.assignments.filter(changes).length : 0;

  const corner = (c: Corner | null) => {
    const name = c ? personName(c.first_name, c.last_name) : '';
    if (!name) return t.common.tbd;
    return <>{name}{c?.team_name && <span className="text-gray-400">{` · ${c.team_name}`}</span>}</>;
  };

  const renderCell = (a: BoutAssignment, row: number, slot: Slot) => {
    const key = String(slot);
    // A side seat of a bout with a kept side panel is locked: Apply All keeps the stored side judge.
    const locked = slot !== 'ring' && a.kept_side_judges;
    // A normal bout has 3 side seats; the column of a 4th or 5th seat is empty.
    if (slot !== 'ring' && !a.kept_side_judges && slot >= PLAN_SIDE_SEATS) {
      return <td key={key} className="px-2 py-1.5 bg-gray-50" />;
    }
    const ref = getSlot(a, slot);
    const conflict = ref?.neutral === false;
    const isOver = dragOver?.row === row && dragOver?.slot === slot;
    const isSrc = drag?.row === row && drag?.slot === slot;
    return (
      <td key={key}
          data-testid={`plan-cell-${a.bout_number}-${key}`}
          data-conflict={conflict ? 'true' : 'false'}
          data-locked={locked ? 'true' : undefined}
          draggable={!!ref && !locked}
          onDragStart={() => setDrag({ row, slot })}
          onDragEnd={() => { setDrag(null); setDragOver(null); }}
          onDragOver={e => { if (drag && !locked) { e.preventDefault(); setDragOver({ row, slot }); } }}
          onDragLeave={() => setDragOver(prev => (prev?.row === row && prev?.slot === slot ? null : prev))}
          onDrop={e => { e.preventDefault(); if (drag && !locked) swapSlots(drag, { row, slot }); setDrag(null); setDragOver(null); }}
          title={locked ? t.planOfficials.lockedTip : conflict ? t.planOfficials.conflictTip : undefined}
          className={`px-2 py-1.5 select-none align-top ${ref ? colourOf(ref.id).cell : ''} ${ref && !locked ? 'cursor-move' : ''} ${conflict ? 'font-semibold' : ''} ${isOver ? 'outline-solid outline-2 outline-blue-500 -outline-offset-2' : ''} ${isSrc ? 'opacity-40' : ''}`}>
        <span className="inline-flex items-start gap-1">
          {conflict && <WarningIcon className="w-3.5 h-3.5 shrink-0 text-amber-700" />}
          {locked && <LockIcon className="w-3 h-3 mt-0.5 shrink-0 opacity-60" />}
          <span className="break-words">{personName(ref?.first_name, ref?.last_name) || '—'}</span>
          {ref?.edited && <span className="inline-block opacity-70" title={t.planOfficials.editedTip}><PencilIcon className="w-2.5 h-2.5" /></span>}
        </span>
      </td>
    );
  };

  // #552 — the shared panel. #589 — the plan table needs the width of a
  // planner: a desktop tool (operator, 2026-10-15); a phone scrolls the table.
  return (
    <>
    <ModalBackdrop onClose={onClose} maxWidth={result ? '5xl' : '2xl'} layout="panel" ariaLabel={t.planOfficials.ariaLabel}>
      <ModalHeader title={t.planOfficials.title} />
        <ModalBody className="space-y-4">
          {!result ? (
            <>
              <Field label={t.common.ring} required>
                <select className="input" value={ring} onChange={e => setRing(e.target.value)}>
                  <option value="">{t.planOfficials.selectRing}</option>
                  {rings.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </Field>
              <Field label={t.planOfficials.rotation} group>
                <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {referees.map(ref => {
                    const index = picked.indexOf(ref.id);
                    return (
                      <label key={ref.id} className="flex items-center gap-2 cursor-pointer py-1">
                        <input type="checkbox" checked={refs.has(ref.id)} data-testid={`plan-rotation-${ref.id}`}
                          onChange={e => setRefs(prev => { const n = new Set(prev); e.target.checked ? n.add(ref.id) : n.delete(ref.id); return n; })}
                          className="w-4 h-4 rounded-sm accent-gray-900" />
                        <span className={`inline-block w-3 h-3 rounded-full border ${index < 0 ? 'border-transparent' : `border-black/10 ${colourAt(index).chip}`}`} title={index < 0 ? undefined : t.planOfficials.colorTip} />
                        <span className="text-sm">{personName(ref.first_name, ref.last_name)}</span>
                        {ref.team_name && <span className="text-[10px] text-gray-400 bg-gray-50 px-1 rounded-sm">{ref.team_name}</span>}
                        {ref.in_jury_pool && <span className="text-purple-600" title={t.planOfficials.juryPoolTip}><ClipboardIcon className="w-3 h-3" /></span>}
                      </label>
                    );
                  })}
                </div>
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={() => setRefs(new Set(referees.map(r => r.id)))} className="btn-ghost btn-xs">{t.planOfficials.selectAll}</button>
                  <button type="button" onClick={() => setRefs(new Set())} className="btn-ghost btn-xs">{t.common.clear}</button>
                </div>
              </Field>
              <Field label={t.planOfficials.ringRefCandidates} group>
                {refs.size === 0 ? (
                  <p className="text-xs text-gray-400">{t.planOfficials.ringRefCandidatesEmpty}</p>
                ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {referees.filter(r => refs.has(r.id)).map(ref => (
                    <label key={ref.id} className="flex items-center gap-2 cursor-pointer py-1">
                      <input type="checkbox" checked={ringRefs.has(ref.id)} data-testid={`plan-candidate-${ref.id}`}
                        onChange={e => setRingRefs(prev => { const n = new Set(prev); e.target.checked ? n.add(ref.id) : n.delete(ref.id); return n; })}
                        className="w-4 h-4 rounded-sm accent-orange-500" />
                      <span className={`inline-block w-3 h-3 rounded-full border border-black/10 ${colourAt(picked.indexOf(ref.id)).chip}`} />
                      <span className="text-sm">{personName(ref.first_name, ref.last_name)}</span>
                    </label>
                  ))}
                </div>
                )}
              </Field>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="badge badge-green text-xs" data-testid="plan-neutral">{t.planOfficials.neutral.replace('{n}', String(result.summary.fully_neutral))}</span>
                {result.summary.has_conflicts && (
                  <span className="badge badge-amber text-xs" data-testid="plan-conflicts">{t.planOfficials.withConflicts.replace('{n}', String(result.summary.total_bouts - result.summary.fully_neutral))}</span>
                )}
                <span className="text-xs text-gray-400">{boutCount(result.summary.total_bouts)}</span>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full min-w-[760px] text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-[10px] uppercase">
                      <th className="px-2 py-1.5 text-left">#</th>
                      <th className="px-2 py-1.5 text-left">{t.viewOfficials.colBout}</th>
                      <th className="px-2 py-1.5 text-left">{t.planOfficials.colRingRef}</th>
                      {Array.from({ length: judgeCols }, (_, i) => i + 1).map(n => (
                        <th key={n} className="px-2 py-1.5 text-left">{t.planOfficials.colJudge.replace('{n}', String(n))}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.assignments.map((a: BoutAssignment, row: number) => {
                      const newDay = !!a.bout_date && (row === 0 || result.assignments[row - 1].bout_date !== a.bout_date);
                      const gender = a.red?.gender || a.blue?.gender;
                      const ageGroup = a.bout_age_group || deriveAgeGroup(a.red?.age_group ?? undefined, a.blue?.age_group ?? undefined);
                      const weightClass = a.bout_weight_class || deriveWeightClass(a.red?.weight_class ?? undefined, a.blue?.weight_class ?? undefined);
                      const nowRing = personName(a.now.ring_referee?.first_name, a.now.ring_referee?.last_name);
                      const nowSides = a.now.side_judges.map(s => personName(s.first_name, s.last_name)).join(', ');
                      const mark = a.warnings.length ? 'border-l-amber-500' : 'border-l-transparent';
                      // #623 — the ring referee changes after this row: the next row of the day ends with another
                      // ring referee (the planned one, else the stored one, as seatedIds() reads it).
                      const next = result.assignments[row + 1];
                      const ringOf = (r: BoutAssignment) => r.ring_referee?.id ?? r.now.ring_referee?.id ?? null;
                      const blockEnd = !!next && next.bout_date === a.bout_date && ringOf(next) !== ringOf(a);
                      return (
                        <Fragment key={a.bout_id}>
                          {newDay && (
                            <tr className="bg-gray-50 border-t border-gray-200" data-testid="plan-day">
                              <td colSpan={3 + judgeCols} className="px-2 py-1 font-semibold text-gray-600">{formatDay(a.bout_date, 'EEE dd MMM')}</td>
                            </tr>
                          )}
                          <tr data-testid={`plan-row-${a.bout_number}`} data-ring-block-end={blockEnd}
                            className={`border-t border-gray-100 ${blockEnd ? 'border-b-2 border-b-gray-400' : ''}`}>
                            <td className={`px-2 py-1.5 align-top border-l-4 ${mark}`}>
                              <div className="font-bold">{a.bout_number}</div>
                              <div className="text-[10px] text-gray-400" title={t.planOfficials.orderTip} data-testid={`plan-order-${a.bout_number}`}>{a.ring_day_order}.</div>
                            </td>
                            <td className="px-2 py-1.5 align-top min-w-[200px]">
                              <div className="text-red-700 break-words">{corner(a.red)}</div>
                              <div className="text-blue-700 break-words">{corner(a.blue)}</div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[10px] text-gray-500" data-testid={`plan-info-${a.bout_number}`}>
                                {a.level && <BoutLevelPill level={a.level} size="xs" />}
                                {gender && <GenderBadge gender={gender.toLowerCase()} short size="xs" className="px-1" />}
                                {ageGroup && <span>{ageGroup}</span>}
                                {weightClass && <span>{weightClass}</span>}
                                {a.jury && <span className="break-words">{`· ${t.bout.jury}: ${personName(a.jury.first_name, a.jury.last_name)}`}</span>}
                              </div>
                              {(nowRing || nowSides) && (
                                <div className="text-[10px] text-gray-500 break-words" data-testid={`plan-now-${a.bout_number}`}>
                                  {`${t.planOfficials.now}: ${nowRing || '—'} · ${nowSides || '—'}`}
                                </div>
                              )}
                            </td>
                            {renderCell(a, row, 'ring')}
                            {Array.from({ length: judgeCols }, (_, k) => renderCell(a, row, k))}
                          </tr>
                          {a.warnings.length > 0 && (
                            <tr data-testid={`plan-reasons-${a.bout_number}`}>
                              <td colSpan={3 + judgeCols} className="px-2 pb-1.5 border-l-4 border-l-amber-500">
                                <ul className="space-y-0.5">
                                  {a.warnings.map((w, i) => (
                                    <li key={i} data-testid="plan-reason" data-code={w.code} className="flex items-start gap-1 text-[11px] text-amber-800">
                                      <WarningIcon className="w-3 h-3 mt-0.5 shrink-0" />
                                      <span>{formatWarning(w)}</span>
                                    </li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* #589 — the load of each official in the current plan: it changes after a swap. */}
              <div data-testid="plan-legend">
                {/* #623 — the blocks of the seats. */}
                <p className="text-[11px] text-gray-500 mb-1" data-testid="plan-blocks-hint">{t.planOfficials.blocksHint}</p>
                <p className="text-[11px] text-gray-500 mb-1">{t.planOfficials.legendTitle}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {legend.map(p => (
                    <span key={p.id} data-testid={`plan-legend-${p.id}`} className="inline-flex items-center gap-1.5 text-xs">
                      <span className={`inline-block w-3 h-3 rounded-sm border border-black/10 ${colourOf(p.id).chip}`} />
                      <span className="break-words">{personName(p.first_name, p.last_name)}</span>
                      {/* #589 — a novice: the planner puts a maximum of one in each bout when it can. */}
                      {novices.has(p.id) && (
                        <span className="text-[10px] font-semibold text-sky-700 bg-sky-50 px-1 rounded-sm border border-sky-200" data-testid="plan-legend-novice">{t.refereeModal.novice}</span>
                      )}
                      <span className="text-gray-500 tabular-nums" data-testid="plan-legend-load">{p.ring} / {p.side}</span>
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </ModalBody>
      {!result ? (
        <ModalFooter
          onSave={generate}
          busy={suggest.busy}
          busyLabel={t.planOfficials.generating}
          saveLabel={t.planOfficials.generate}
          saveDisabled={!ring || refs.size < 3}
          onCancel={onClose}
        />
      ) : (
        <ModalFooter
          secondary={<button type="button" onClick={back} disabled={applyBusy.busy} className="btn-outline btn-sm">{t.common.back}</button>}
          beside={(
            <button type="button" onClick={() => apply(true)} data-testid="plan-apply-ring-refs"
              disabled={applyBusy.busy || !result.assignments.some(a => a.ring_referee)} className="btn-outline btn-sm">
              {applying === 'ring' ? t.common.saving : t.planOfficials.applyRingRefs}
            </button>
          )}
          // #589 — Apply All first asks: it says how many bouts change officials.
          onSave={() => setConfirmAll(true)}
          busy={applying === 'all'}
          saveLabel={t.planOfficials.applyAll}
          // #588 — a ring with no pending bout gives an empty plan: nothing to apply.
          saveDisabled={applyBusy.busy || result.assignments.length === 0}
          onCancel={onClose}
        />
      )}
    </ModalBackdrop>
    {confirmAll && result && (
      <ConfirmModal
        title={t.planOfficials.applyAll}
        message={countLabel(lang, changeCount, { one: t.planOfficials.confirmOne, few: t.planOfficials.confirmFew, other: t.planOfficials.confirmOther })}
        confirmLabel={t.planOfficials.applyAll}
        busy={applyBusy.busy}
        testId="plan-apply-confirm"
        onConfirm={() => { void apply(false); }}
        onCancel={() => { if (!applyBusy.busy) setConfirmAll(false); }}
      />
    )}
    </>
  );
}
